import {
    observer
} from 'mobx-react-lite';
import type { ApplicationSdk } from '../stores';
import { Button } from 'primereact/button';
import SignatureCanvas from 'react-signature-canvas';
import { useState, useRef, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import '../sign.css';
import DocumentStore from '../stores/DocumentStore';

const A4_WIDTH = 595.28;  // Punkte = 210mm
const A4_HEIGHT = 841.89; // Punkte = 297mm

const DocumentView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const [showSignature, setShowSignature] = useState(false);
    const [signatureDataURL, setSignatureDataURL] = useState(null);

    const getCanvasProps = () => {
        // Sichtbare Größe (CSS)
        const cssWidth = Math.min(window.innerWidth - 40, 500); // 40px für Padding/Margin
        const cssHeight = 200;
        const ratio = window.devicePixelRatio || 1;
        return {
            width: cssWidth,   // Canvas-Pixelgröße (scharf auf Retina)
            height: cssHeight,
            style: {
                width: `${cssWidth}px`,   // Sichtbare Größe (immer px, nie %!)
                height: `${cssHeight}px`,
                border: '1px solid #ccc',
                borderRadius: 4,
                touchAction: 'none',
                background: '#fff'
            },
            className: "signature-canvas"
        };
    };

    useEffect(() => {
        sdk.useStore(DocumentStore);
    }, [sdk]);

    // Dynamisch skalieren auf Retina-Displays
    // useEffect(() => {
    //     //@ts-ignore
    //     const canvas = signatureRef.current?.getCanvas();
    //     if (canvas) {
    //         const ratio = window.devicePixelRatio || 1;
    //         const context = canvas.getContext("2d");

    //         // WICHTIG: Skalierung nur setzen, wenn noch nicht gesetzt
    //         if (context.getTransform().a === 1) {
    //             context.scale(ratio, ratio);
    //         }
    //     }
    // }, []);

    const handleSignClick = () => {
        setShowSignature(true);
    };

    const handleClear = () => {
        if (signatureRef.current) {
            // @ts-ignore
            signatureRef.current.clear();
        }
        setSignatureDataURL(null);
    };

    const exportToPDF = async () => {
        if (!currentStore?.pdf_file || !signatureRef.current) {
            alert('Bitte wähle eine PDF-Datei aus und füge eine Unterschrift hinzu!');
            return;
        }

        try {
            // PDF laden
            const pdfBytes = await currentStore.pdf_file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(pdfBytes);

            // Canvas (Unterschrift) als Bild exportieren
            // @ts-ignore
            const signatureDataUrl = signatureRef.current.toDataURL();
            const signatureImage = await pdfDoc.embedPng(signatureDataUrl);

            // Füge die Unterschrift zur ersten Seite hinzu
            const pages = pdfDoc.getPages();

            const firstPage = pages[0];
            const secondPage = pages?.[1] || null;
            const { width, height } = firstPage.getSize();

            // Unterschrift Position 1
            if (secondPage) {
                secondPage.drawImage(signatureImage, {
                    x: width - 520, // 150px vom rechten Rand
                    y: 50,          // 50px vom unteren Rand
                    width: 140,
                    height: 70
                });
            } else {
                firstPage.drawImage(signatureImage, {
                    x: width - 520, // 150px vom rechten Rand
                    y: 60,          // 50px vom unteren Rand
                    width: 140,
                    height: 70
                });
            }
            // firstPage.drawImage(signatureImage, {
            //     x: width - 520, // 150px vom rechten Rand
            //     y: 640,          // 50px vom unteren Rand
            //     width: 200,
            //     height: 100
            // });

            // PDF speichern
            const mergedPdfBytes = await pdfDoc.save();
            const mergedPdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

            // Convert Blob to Buffer if necessary
            if(await currentStore.signDocument(mergedPdfBlob)) {
                window.location.href = `https://simply-scan.me/success-aktive-schuldenhilfe/`;
            }
            setShowSignature(false);
        } catch (error) {
            console.error('Fehler beim Zusammenführen:', error);
            alert('Ein Fehler ist aufgetreten!');
        }
    };

    const iframeRef = useRef(null);
    const signatureRef = useRef(null);
    const currentStore = sdk.currentStore;

    return (
        <div className="container">
            <h1 className="title">Dokument unterzeichnen</h1>
            <div className="pdf-wrapper" style={{ position: 'relative', width: '100%', maxWidth: 600, margin: '0 auto' }}>
                {currentStore?.pdf_file && (
                    <iframe
                        title="html-preview"
                        ref={iframeRef}
                        src={URL.createObjectURL(currentStore?.pdf_file_signed || currentStore?.pdf_file || '')}
                        className="doc-iframe"
                        style={{
                            width: '100%',
                            aspectRatio: '1 / 1.414',
                            border: '1px solid #ccc',
                            borderRadius: 8,
                            background: '#fff',
                            display: 'block'
                        }}
                    />
                )}
                {showSignature && (
                    <div
                        className="signature-box"
                        style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: '5%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(255,255,255,0.95)',
                            padding: 12,
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            width: '90%',
                            maxWidth: 500,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <h4 style={{ fontSize: '1.1rem', marginBottom: 8 }}>Bitte unterschreibe unten:</h4>
                        <SignatureCanvas
                            penColor="black"
                            canvasProps={getCanvasProps()}

                            ref={signatureRef}
                        />
                        <div style={{ marginTop: 12, display: 'flex', gap: 8, width: '100%' }}>
                            <Button label="Löschen" icon="pi pi-times" onClick={handleClear} className="p-button-text" style={{ flex: 1 }} />
                            <Button label="Unterschrift speichern" icon="pi pi-check" onClick={async () => {
                                await exportToPDF();
                                window.close();
                            }} className="p-button-success" style={{ flex: 2 }} />
                        </div>
                    </div>
                )}
            </div>
            {!showSignature && (
                <div style={{ marginTop: '2rem' }}>
                    <Button label="Unterschreiben" icon="pi pi-pencil" onClick={handleSignClick} style={{ width: '100%' }} />
                </div>
            )}
            {currentStore?.pdf_signed_url && (
                <div style={{ marginTop: '1rem' }}>
                    <a href={currentStore?.pdf_signed_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '1rem', wordBreak: 'break-all' }}>Signiertes PDF ansehen</a>
                </div>
            )}
        </div>
    );
});

// const DocumentView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
//     const [showSignature, setShowSignature] = useState(false);
//     const [signatureDataURL, setSignatureDataURL] = useState(null);
//     const [signedPdfUrl, setSignedPdfUrl] = useState(null);
//     const [documentHtml, setDocumentHtml] = useState<string>(`<!DOCTYPE html>
// <html lang="de">
// <head>
//   <meta charset="UTF-8">
//   <title>Bestätigung Warenerhalt</title>
//   <style>
//     body {
//       font-family: Arial, sans-serif;
//       margin: 2em;
//     }
//     .inhalt {
//       max-width: 600px;
//       margin: 0 auto;
//     }
//     .unterschrift {
//       margin-top: 4em;
//     }
//     .signatur-box {
//       width: 100%;
//       height: 100px;
//       border: 1px solid #000;
//       margin-top: 1em;
//     }
//   </style>
// </head>
// <body>
//   <div class="inhalt">
//     <p>Hiermit bestätige ich, {{name}}, den Erhalt der Ware "Steuertraining".</p>

//     <div class="unterschrift">
//       <p>Unterschrift:</p>
//       <div class="signatur-box"></div>
//     </div>
//   </div>
// </body>
// </html>
// `);
//     const signatureRef = useRef(null);
//     const iframeRef = useRef(null);


//     useEffect(() => {
//         //@ts-ignore
//         const doc = iframeRef.current.contentDocument;
//         doc.open();
//         doc.write(documentHtml);
//         doc.close();
//     }, [documentHtml]);

//     const handleSignClick = () => {
//         setShowSignature(true);
//     };

//     const handleClear = () => {
//         if (signatureRef.current) {
//             // @ts-ignore
//             signatureRef.current.clear();
//         }
//         setSignatureDataURL(null);
//     };

//     const handleSaveSignature = async () => {
//         try {
//             const container = document.getElementById('vertrag-container');
//             if (!container) {
//                 throw new Error('Vertrag nicht gefunden');
//             }

//             // Render HTML zu Canvas (hohe Auflösung)
//             const canvas = await html2canvas(container, {
//                 scale: 2,
//                 backgroundColor: '#fff',
//                 useCORS: true,
//                 allowTaint: true,
//             });

//             // PDF vorbereiten (A4)
//             const pdfDoc = await PDFDocument.create();
//             const a4Width = 595.28;  // pt
//             const a4Height = 841.89; // pt

//             // Bilddaten und Maße
//             const imgData = canvas.toDataURL('image/png');
//             const pngImage = await pdfDoc.embedPng(imgData);
//             const imgWidth = canvas.width;
//             const imgHeight = canvas.height;

//             // Skalierung auf A4-Breite
//             const scale = a4Width / imgWidth;
//             const scaledHeight = imgHeight * scale;

//             // Mehrseitig, falls nötig
//             let offsetY = 0;
//             while (offsetY < scaledHeight) {
//                 const page = pdfDoc.addPage([a4Width, a4Height]);
//                 const pageHeight = Math.min(a4Height, scaledHeight - offsetY);

//                 page.drawImage(pngImage, {
//                     x: 0,
//                     width: a4Width,
//                     height: a4Height,
//                     // Der sichtbare Bereich wird durch y und pageHeight gesteuert
//                 });

//                 offsetY += pageHeight;
//             }

//             // PDF speichern
//             const pdfBytes = await pdfDoc.save();
//             const blob = new Blob([pdfBytes], { type: 'application/pdf' });
//             const url = URL.createObjectURL(blob);
//             //@ts-ignore
//             setSignedPdfUrl(url);

//         } catch (error: any) {
//             console.error('Fehler beim Generieren des PDFs:', error);
//             alert('Fehler beim Erstellen des PDFs: ' + error.message);
//         }
//     };


//     const exportToPDF = async () => {
//         //@ts-ignore
//         const iframeDoc = iframeRef.current.contentDocument;
//         const body = iframeDoc.body;

//         // Screenshot erstellen
//         const canvas = await html2canvas(body, { scale: 2 });
//         const imgData = canvas.toDataURL('image/png');

//         // pdf-lib Dokument vorbereiten
//         const pdfDoc = await PDFDocument.create();
//         const pngImage = await pdfDoc.embedPng(imgData);

//         const imgWidth = pngImage.width;
//         const imgHeight = pngImage.height;

//         // Verhältnis berechnen, um auf A4 zu passen (Skalierung)
//         const scale = A4_WIDTH / imgWidth;
//         const scaledHeight = imgHeight * scale;

//         // Anzahl A4-Seiten
//         const pageCount = Math.ceil(scaledHeight / A4_HEIGHT);

//         for (let i = 0; i < pageCount; i++) {
//             const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);

//             const yOffset = A4_HEIGHT * i;
// console.log(`Seite ${i + 1} von ${pageCount}, yOffset: ${yOffset}`);
//             console.log(pngImage)
//             page.drawImage(pngImage, {
//                 x: 0,
//                 y: -yOffset,
//                 width: A4_WIDTH,
//                 height: scaledHeight,
//             });

//         }

//         const pdfBytes = await pdfDoc.save();
//         const blob = new Blob([pdfBytes], { type: 'application/pdf' });

//         const link = document.createElement('a');
//         link.href = URL.createObjectURL(blob);
//         link.download = 'html-vorschau.pdf';
//         link.click();
//     };


//     const handlePdfUpload = (e: any) => {
//         const file = e.target.files[0];
//         if (file && file.type === 'application/pdf') {
//             alert(`PDF "${file.name}" wurde hochgeladen.`);
//             // Hier kannst du die Datei an deinen Server senden
//         }
//     };

//     return <div style={{ padding: '2rem', maxWidth: 800, margin: '0 auto', height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
//         <h2>Vertragsdokument</h2>

//         <iframe
//             title="html-preview"
//             ref={iframeRef}
//             style={{ width: '100%', height: '400px', border: '1px solid #ccc' }}
//         />

// {!showSignature && (
//     <div style={{ marginTop: '2rem' }}>
//         <Button label="Unterschreiben" icon="pi pi-pencil" onClick={handleSignClick} />
//     </div>
// )}

// {showSignature && (
//     <div style={{ marginTop: '2rem' }}>
//         <h4>Bitte unterschreibe unten:</h4>
//         <SignatureCanvas
//             penColor="black"
//             canvasProps={{
//                 width: 500,
//                 height: 200,
//                 className: 'sigCanvas',
//                 style: { border: '1px solid #ccc', borderRadius: 4 }
//             }}
//             ref={signatureRef}
//         />
//         <div style={{ marginTop: '1rem' }}>
//             <Button label="Löschen" icon="pi pi-times" onClick={handleClear} className="p-button-text" />
//             <Button label="Unterschrift speichern" icon="pi pi-check" onClick={exportToPDF} className="p-button-success" style={{ marginLeft: '1rem' }} />
//         </div>
//     </div>
// )}
//         {signedPdfUrl && (
//             <div style={{ marginTop: '1rem' }}>
//                 <a href={signedPdfUrl} target="_blank" rel="noopener noreferrer">Signiertes PDF ansehen</a>
//             </div>
//         )}
//     </div>
// });

export default DocumentView;